import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Col, Row, Card, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AsyncSelect from 'components/common/search/AsyncSelect';
import fleetServices from 'services/fleet.services';
import { useAuth } from 'hooks/useAuth';
import useFetchFleet from 'pages/Fleet/services/useFetchFleet';
import SingleNote from 'components/SingleNote';
import AuthenticationWrapper from 'components/authentication/AuthenticationWrapper';
import { ROLES } from 'constants/index';
import { useFleetContext } from 'pages/Fleet/FleetContext';

export default function SupervisorAndNotes() {
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { viewAsRole } = useAuth();
  const { fleetId, loadingDetail, data } = useFleetContext();
  const { fleetSupervisor, fetchFleetSupervisor } = useFetchFleet();
  const [dataUser, setDataUser] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const isAdmin = viewAsRole === ROLES.ADMIN;

  const onUpdateNote = async formValue => {
    try {
      await fleetServices.updateFleet(fleetId, { ...data, ...formValue });
    } catch (err) {
      console.error(err);
    }
  };

  const onSelectItem = async item => {
    try {
      const newUser = { id: item.value, name: item.label, email: item?.email };
      await fleetServices.assignToFleetSupervisor(fleetId, item.value);
      setDataUser(dataUser.filter(record => record.id !== item.value));
      setSelectedUser([...selectedUser, newUser]);
      toast.success('Assign supervisors successfully!', {
        theme: 'colored'
      });
    } catch (err) {
      console.error(err.message);
      toast.error('Assign supervisors failed!', {
        theme: 'colored'
      });
    }
  };

  const fetchAvailableSupervisor = inputValue => {
    return fleetServices.getListAvailableToSupervisor(fleetId, {
      user_name: inputValue
    });
  };

  const onDeleteSuperVisor = async supervisorId => {
    try {
      if (loadingDelete) return;
      setLoadingDelete(true);
      await fleetServices.deleteFleetSupervisor(fleetId, supervisorId);
      toast.success('The supervisor was successfully deleted!');
      await fetchFleetSupervisor(fleetId);
    } catch (err) {
      toast.error(err?.error?.message || 'Could not delete the supervisor.');
    } finally {
      setLoadingDelete(false);
    }
  };

  useEffect(() => {
    setSelectedUser(fleetSupervisor);
  }, [fleetSupervisor]);

  useEffect(() => {
    if (fleetId) {
      fetchFleetSupervisor(fleetId);
    }
  }, [fleetId]);

  return (
    <Row className="g-3 supervisor-note">
      <Col xs={12} lg={6}>
        <Card className="mb-3 align-self-stretch flex-1 single-note">
          <>
            <Card.Header className="d-flex justify-content-between">
              <h5 className="fs-0 d-inline-flex align-items-center mb-0">Supervisor</h5>
              <AuthenticationWrapper
                viewAsRole={viewAsRole}
                permissions={[ROLES.ADMIN, ROLES.CABFARE_INTERNAL]}
              >
                <AsyncSelect
                  value={null}
                  placeholder="Search..."
                  fetchFn={fetchAvailableSupervisor}
                  onChange={onSelectItem}
                  wrapperClassname="ms-2"
                  listFilteredItem={selectedUser.map(user => user.id)}
                />
              </AuthenticationWrapper>
            </Card.Header>
            <Card.Body className="border-top p-0">
              <Table responsive size="sm" className="fs--1 table-responsive visible-scrollbar">
                <thead>
                  <tr>
                    <th className="text-900">Name</th>
                    <th className="text-900">Email</th>
                    <th className="text-900 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUser.map(item => (
                    <tr key={item.id} className="fs--1">
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td className="text-center">
                        {isAdmin && (
                          <FontAwesomeIcon
                            icon="trash"
                            className={classNames('ms-1 fs--1', {
                              'opacity-25': loadingDelete,
                              'cursor-pointer': !loadingDelete
                            })}
                            onClick={() => onDeleteSuperVisor(item.id)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer className="border-top text-end"></Card.Footer>
          </>
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <SingleNote
          name="notes"
          titleClass="fs-0"
          title="Fleet Note"
          value={data?.notes}
          loadingInformation={loadingDetail}
          handleUpdate={onUpdateNote}
        />
      </Col>
    </Row>
  );
}
